
import { useState, useCallback, useEffect } from 'react'
import { StageWrapper } from '@/features/enrollment/core/StageWrapper'
import type { ValidationErrors } from '@/features/enrollment'

const MARITAL_OPTIONS = [
    { value: '1', label: 'No' },
    { value: '2', label: 'Yes "Married File Separate"' },
    { value: '3', label: 'Yes "Married File Joint"' }
]

interface Dependent {
    id: string
    firstName: string
    lastName: string
    dob: string
    ssn: string
    onPolicy: string
}

interface EnrollmentDataStepProps {
    enrollmentData: any
    updateEnrollmentData: (field: string, value: any) => void
    handleNavigate: (targetIndex: number) => void | Promise<void>
    handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
    isNavigating: boolean
    currentStepIndex?: number
    totalSteps?: number
    [key: string]: any
}

export default function EnrollmentDataStep({
    enrollmentData,
    updateEnrollmentData,
    handleNavigate,
    handleNavigateWithCompletion,
    isNavigating,
    currentStepIndex,
    totalSteps = 6
}: EnrollmentDataStepProps) {
    const [errors, setErrors] = useState<ValidationErrors>({})
    const [activeDependent, setActiveDependent] = useState<Dependent | null>(null)
    const [isEditingDependent, setIsEditingDependent] = useState(false)
    const [dependentFormErrors, setDependentFormErrors] = useState<{ [key: string]: string }>({})

    const hasDependents = enrollmentData.hasDependents
    const dependents: Dependent[] = enrollmentData.dependentsList || []
    const maritalStatus = enrollmentData.maritalStatus

    // Calculate household size based on spouse and dependents
    useEffect(() => {
        let count = 1 // Client

        // Add spouse if married file joint
        if (maritalStatus === '3') {
            count += 1
        }

        // Add dependents on policy
        if (dependents && Array.isArray(dependents)) {
            count += dependents.filter(dep => dep.onPolicy === 'Yes').length
        }

        updateEnrollmentData('householdSize', count)
    }, [maritalStatus, dependents])

    const handleNext = useCallback(async (): Promise<boolean> => {
        const validationErrors: ValidationErrors = {}

        // Enrollment data validation
        if (!enrollmentData.clientAddress) validationErrors.clientAddress = 'Address is required'
        if (!enrollmentData.clientCity) validationErrors.clientCity = 'City is required'
        if (!enrollmentData.clientPhone) validationErrors.clientPhone = 'Phone is required'
        if (!enrollmentData.clientEmail) validationErrors.clientEmail = 'Email is required'
        if (!enrollmentData.clientSex) validationErrors.clientSex = 'Sex is required'
        if (!enrollmentData.isSmoker) validationErrors.isSmoker = 'Smoker status is required'
        if (enrollmentData.clientSex === 'Female' && !enrollmentData.isPregnant) {
            validationErrors.isPregnant = 'Pregnancy status is required'
        }

        // Spousal data validation
        if (!maritalStatus) {
            validationErrors.maritalStatus = 'Please select marital status'
        } else if (maritalStatus === '3') {
            if (!enrollmentData.spouseFirstName) validationErrors.spouseFirstName = 'Spouse first name is required'
            if (!enrollmentData.spouseLastName) validationErrors.spouseLastName = 'Spouse last name is required'
            if (!enrollmentData.spouseDOB) validationErrors.spouseDOB = 'Spouse date of birth is required'
            if (!enrollmentData.spouseSSN) {
                validationErrors.spouseSSN = 'Spouse SSN is required'
            } else if (enrollmentData.spouseSSN.replace(/\D/g, '').length !== 9) {
                validationErrors.spouseSSN = 'Spouse SSN must be 9 digits'
            }
        }

        // Dependents validation
        if (!hasDependents) {
            validationErrors.hasDependents = 'Please select dependent status'
        } else if (hasDependents === 'Yes "I File Them On My Taxes"' && dependents.length === 0 && !isEditingDependent) {
            validationErrors.hasDependents = 'Please add at least one dependent or finish editing'
        }

        setErrors(validationErrors)
        return Object.keys(validationErrors).length === 0
    }, [enrollmentData, maritalStatus, hasDependents, dependents, isEditingDependent])

    const handlePhoneChange = (val: string) => {
        const nums = val.replace(/\D/g, '').substring(0, 10)
        let formatted = nums
        if (nums.length > 6) {
            formatted = `(${nums.substring(0, 3)}) ${nums.substring(3, 6)}-${nums.substring(6, 10)}`
        } else if (nums.length > 3) {
            formatted = `(${nums.substring(0, 3)}) ${nums.substring(3, 6)}`
        } else if (nums.length > 0) {
            formatted = `(${nums.substring(0, 3)}`
        }
        updateEnrollmentData('clientPhone', formatted)
    }

    const handleSpouseSSNChange = (val: string) => {
        const nums = val.replace(/\D/g, '').substring(0, 9)
        let formatted = nums
        if (nums.length > 5) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}-${nums.substring(5, 9)}`
        } else if (nums.length > 3) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}`
        }
        updateEnrollmentData('spouseSSN', formatted)
    }

    const handleDependentSSNChange = (val: string) => {
        if (!activeDependent) return
        const nums = val.replace(/\D/g, '').substring(0, 9)
        let formatted = nums
        if (nums.length > 5) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}-${nums.substring(5, 9)}`
        } else if (nums.length > 3) {
            formatted = `${nums.substring(0, 3)}-${nums.substring(3, 5)}`
        }
        setActiveDependent({ ...activeDependent, ssn: formatted })
    }

    const handleAddDependentStart = () => {
        setActiveDependent({
            id: Date.now().toString(),
            firstName: '',
            lastName: '',
            dob: '',
            ssn: '',
            onPolicy: 'Yes'
        })
        setIsEditingDependent(true)
        setDependentFormErrors({})
    }

    const handleSaveDependent = () => {
        if (!activeDependent) return

        const newFormErrors: { [key: string]: string } = {}

        if (!activeDependent.firstName) newFormErrors.firstName = 'First name is required'
        if (!activeDependent.lastName) newFormErrors.lastName = 'Last name is required'
        if (!activeDependent.dob) newFormErrors.dob = 'Date of birth is required'

        if (activeDependent.onPolicy === 'Yes') {
            if (!activeDependent.ssn) {
                newFormErrors.ssn = 'SSN is required when dependent is on policy'
            } else if (activeDependent.ssn.replace(/\D/g, '').length !== 9) {
                newFormErrors.ssn = 'SSN must be 9 digits'
            }
        }

        if (Object.keys(newFormErrors).length > 0) {
            setDependentFormErrors(newFormErrors)
            return
        }

        const newList = [...dependents, activeDependent]
        updateEnrollmentData('dependentsList', newList)
        setIsEditingDependent(false)
        setActiveDependent(null)
        setDependentFormErrors({})
    }

    const handleDeleteDependent = (id: string) => {
        const newList = dependents.filter(d => d.id !== id)
        updateEnrollmentData('dependentsList', newList)
    }

    const handleCancelDependentEdit = () => {
        setIsEditingDependent(false)
        setActiveDependent(null)
        setDependentFormErrors({})
    }

    // Auto-show dependent entry form when option 3 is selected
    useEffect(() => {
        if (hasDependents === 'Yes "I File Them On My Taxes"' && dependents.length === 0 && !isEditingDependent) {
            handleAddDependentStart()
        }
    }, [hasDependents])

    return (
        <StageWrapper
            currentStepIndex={currentStepIndex ?? 1}
            totalSteps={totalSteps}
            handleNavigate={handleNavigate}
            handleNavigateWithCompletion={handleNavigateWithCompletion}
            onNext={handleNext}
            isNavigating={isNavigating}
        >
            <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-xl font-semibold text-accent-900">Step 2: Enrollment Data</h3>
                        <p className="text-sm text-accent-600">Contact info, spouse, and dependents.</p>
                    </div>
                </div>

                <div className="space-y-8">
                    {/* SECTION: Contact Information */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Contact Information</h4>
                        <div className="space-y-4">
                            {/* Address */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Client Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={enrollmentData.clientAddress || ''}
                                    onChange={(e) => updateEnrollmentData('clientAddress', e.target.value)}
                                    placeholder="Street Address"
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientAddress ? 'border-red-500' : 'border-gray-300'}`}
                                />
                                {errors.clientAddress && <p className="mt-1 text-sm text-red-600">{errors.clientAddress}</p>}
                                <input
                                    type="text"
                                    value={enrollmentData.clientAddressLine2 || ''}
                                    onChange={(e) => updateEnrollmentData('clientAddressLine2', e.target.value)}
                                    placeholder="Apartment, Suite, Unit, etc. (Optional)"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                                />
                            </div>

                            {/* City, State, Zip */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">City <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientCity || ''}
                                        onChange={(e) => updateEnrollmentData('clientCity', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientCity ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.clientCity && <p className="mt-1 text-sm text-red-600">{errors.clientCity}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientState || ''}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                                    <input
                                        type="text"
                                        value={enrollmentData.clientZip || ''}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed"
                                    />
                                </div>
                            </div>

                            {/* Phone and Email */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Phone Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="tel"
                                        value={enrollmentData.clientPhone || ''}
                                        onChange={(e) => handlePhoneChange(e.target.value)}
                                        placeholder="(555) 555-5555"
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientPhone ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.clientPhone && <p className="mt-1 text-sm text-red-600">{errors.clientPhone}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Email Address <span className="text-red-500">*</span></label>
                                    <input
                                        type="email"
                                        value={enrollmentData.clientEmail || ''}
                                        onChange={(e) => updateEnrollmentData('clientEmail', e.target.value)}
                                        placeholder="email@example.com"
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientEmail ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors.clientEmail && <p className="mt-1 text-sm text-red-600">{errors.clientEmail}</p>}
                                </div>
                            </div>

                            {/* Sex */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Client Sex <span className="text-red-500">*</span></label>
                                <select
                                    value={enrollmentData.clientSex || ''}
                                    onChange={(e) => updateEnrollmentData('clientSex', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.clientSex ? 'border-red-500' : 'border-gray-300'}`}
                                >
                                    <option value="">Select Sex</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                                {errors.clientSex && <p className="mt-1 text-sm text-red-600">{errors.clientSex}</p>}
                            </div>

                            {/* Radio Groups */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Smoker <span className="text-red-500">*</span></label>
                                    <div className="flex space-x-6">
                                        <label className="flex items-center">
                                            <input type="radio" value="Yes" checked={enrollmentData.isSmoker === 'Yes'} onChange={() => updateEnrollmentData('isSmoker', 'Yes')} className="form-radio h-4 w-4 text-indigo-600" />
                                            <span className="ml-2 text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center">
                                            <input type="radio" value="No" checked={enrollmentData.isSmoker === 'No'} onChange={() => updateEnrollmentData('isSmoker', 'No')} className="form-radio h-4 w-4 text-indigo-600" />
                                            <span className="ml-2 text-gray-700">No</span>
                                        </label>
                                    </div>
                                    {errors.isSmoker && <p className="mt-1 text-sm text-red-600">{errors.isSmoker}</p>}
                                    {enrollmentData.isSmoker === 'Yes' && (
                                        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                                            <p className="text-sm text-red-700 font-medium">Disqualification Warning: Smokers may not be eligible for this insurance plan.</p>
                                        </div>
                                    )}
                                </div>

                                {enrollmentData.clientSex === 'Female' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Pregnant <span className="text-red-500">*</span></label>
                                        <div className="flex space-x-6">
                                            <label className="flex items-center">
                                                <input type="radio" value="Yes" checked={enrollmentData.isPregnant === 'Yes'} onChange={() => updateEnrollmentData('isPregnant', 'Yes')} className="form-radio h-4 w-4 text-indigo-600" />
                                                <span className="ml-2 text-gray-700">Yes</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input type="radio" value="No" checked={enrollmentData.isPregnant === 'No'} onChange={() => updateEnrollmentData('isPregnant', 'No')} className="form-radio h-4 w-4 text-indigo-600" />
                                                <span className="ml-2 text-gray-700">No</span>
                                            </label>
                                        </div>
                                        {errors.isPregnant && <p className="mt-1 text-sm text-red-600">{errors.isPregnant}</p>}
                                        {enrollmentData.isPregnant === 'Yes' && (
                                            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                                                <p className="text-sm text-red-700 font-medium">Disqualification Warning: Pregnant individuals may not be eligible for this insurance plan.</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* SECTION: Spousal Data */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Spousal Information</h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Married <span className="text-red-500">*</span></label>
                                <div className="space-y-2">
                                    {MARITAL_OPTIONS.map(opt => (
                                        <div key={opt.value} className="flex items-center">
                                            <input
                                                type="radio"
                                                id={`married-${opt.value}`}
                                                name="maritalStatus"
                                                value={opt.value}
                                                checked={enrollmentData.maritalStatus === opt.value}
                                                onChange={(e) => updateEnrollmentData('maritalStatus', e.target.value)}
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            <label htmlFor={`married-${opt.value}`} className="ml-3 block text-sm font-medium text-gray-700">
                                                {opt.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {errors.maritalStatus && <p className="mt-1 text-sm text-red-600">{errors.maritalStatus}</p>}
                            </div>

                            {/* Spouse Details - Only show when Option 3 (Married File Joint) is selected */}
                            {maritalStatus === '3' && (
                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                    <h5 className="text-md font-semibold text-accent-800 mb-4">Spouse Details</h5>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse First Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={enrollmentData.spouseFirstName || ''}
                                                    onChange={(e) => updateEnrollmentData('spouseFirstName', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseFirstName ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseFirstName && <p className="mt-1 text-sm text-red-600">{errors.spouseFirstName}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse Last Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={enrollmentData.spouseLastName || ''}
                                                    onChange={(e) => updateEnrollmentData('spouseLastName', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseLastName ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseLastName && <p className="mt-1 text-sm text-red-600">{errors.spouseLastName}</p>}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse Date of Birth <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={enrollmentData.spouseDOB || ''}
                                                    onChange={(e) => updateEnrollmentData('spouseDOB', e.target.value)}
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseDOB ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseDOB && <p className="mt-1 text-sm text-red-600">{errors.spouseDOB}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Spouse SSN <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={enrollmentData.spouseSSN || ''}
                                                    onChange={(e) => handleSpouseSSNChange(e.target.value)}
                                                    placeholder="###-##-####"
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.spouseSSN ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {errors.spouseSSN && <p className="mt-1 text-sm text-red-600">{errors.spouseSSN}</p>}
                                            </div>
                                        </div>
                                        <p className="text-sm text-blue-600">Household count has been increased by 1 for spouse.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SECTION: Dependents */}
                    <div>
                        <h4 className="text-md font-semibold text-accent-800 mb-4 pb-2 border-b">Dependents</h4>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Dependents <span className="text-red-500">*</span></label>
                                <div className="space-y-2">
                                    {['No', 'Yes "I Do Not Claim Them On My Taxes"', 'Yes "I File Them On My Taxes"'].map(opt => (
                                        <div key={opt} className="flex items-center">
                                            <input
                                                type="radio"
                                                id={`dep-${opt}`}
                                                name="hasDependents"
                                                value={opt}
                                                checked={enrollmentData.hasDependents === opt}
                                                onChange={(e) => updateEnrollmentData('hasDependents', e.target.value)}
                                                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            <label htmlFor={`dep-${opt}`} className="ml-3 block text-sm font-medium text-gray-700">
                                                {opt}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {errors.hasDependents && <p className="mt-1 text-sm text-red-600">{errors.hasDependents}</p>}
                            </div>

                            {/* Only show dependent form for option 3 */}
                            {hasDependents === 'Yes "I File Them On My Taxes"' && (
                                <div className="pt-4 border-t border-gray-200 space-y-4">
                                    {/* List of added dependents */}
                                    {dependents.length > 0 && (
                                        <div className="space-y-2">
                                            <h5 className="text-sm font-medium text-gray-700">Added Dependents ({dependents.length})</h5>
                                            {dependents.map((dep) => (
                                                <div key={dep.id} className="flex items-center justify-between bg-gray-50 p-4 rounded-md">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{dep.firstName} {dep.lastName}</p>
                                                        <p className="text-sm text-gray-500">DOB: {dep.dob} | On Policy: {dep.onPolicy}</p>
                                                    </div>
                                                    <button onClick={() => handleDeleteDependent(dep.id)} className="text-red-600 hover:text-red-800 text-sm font-medium">Remove</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add/Edit Form */}
                                    {isEditingDependent && activeDependent ? (
                                        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                                            <h5 className="text-sm font-medium text-indigo-600 mb-4">New Dependent</h5>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mb-4">
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder="First Name*"
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                                        value={activeDependent.firstName}
                                                        onChange={(e) => setActiveDependent({ ...activeDependent, firstName: e.target.value })}
                                                    />
                                                    {dependentFormErrors.firstName && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.firstName}</p>}
                                                </div>
                                                <div>
                                                    <input
                                                        type="text"
                                                        placeholder="Last Name*"
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                                        value={activeDependent.lastName}
                                                        onChange={(e) => setActiveDependent({ ...activeDependent, lastName: e.target.value })}
                                                    />
                                                    {dependentFormErrors.lastName && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.lastName}</p>}
                                                </div>
                                                <div>
                                                    <input
                                                        type="date"
                                                        placeholder="Date of Birth*"
                                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.dob ? 'border-red-500' : 'border-gray-300'}`}
                                                        value={activeDependent.dob}
                                                        onChange={(e) => setActiveDependent({ ...activeDependent, dob: e.target.value })}
                                                    />
                                                    {dependentFormErrors.dob && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.dob}</p>}
                                                </div>
                                                {activeDependent.onPolicy === 'Yes' && (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            placeholder="SSN*"
                                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${dependentFormErrors.ssn ? 'border-red-500' : 'border-gray-300'}`}
                                                            value={activeDependent.ssn}
                                                            onChange={(e) => handleDependentSSNChange(e.target.value)}
                                                        />
                                                        {dependentFormErrors.ssn && <p className="mt-1 text-sm text-red-600">{dependentFormErrors.ssn}</p>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mb-4">
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Will Dependent Be On Policy? <span className="text-red-500">*</span></label>
                                                <div className="flex space-x-4">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            checked={activeDependent.onPolicy === 'Yes'}
                                                            onChange={() => setActiveDependent({ ...activeDependent, onPolicy: 'Yes', ssn: '' })}
                                                            className="form-radio"
                                                        />
                                                        <span className="ml-2 text-sm">Yes</span>
                                                    </label>
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            checked={activeDependent.onPolicy === 'No'}
                                                            onChange={() => setActiveDependent({ ...activeDependent, onPolicy: 'No', ssn: '' })}
                                                            className="form-radio"
                                                        />
                                                        <span className="ml-2 text-sm">No</span>
                                                    </label>
                                                </div>
                                                {activeDependent.onPolicy === 'Yes' && (
                                                    <p className="text-sm text-blue-600 mt-2">This dependent will be added to the household count.</p>
                                                )}
                                            </div>
                                            <div className="flex justify-end space-x-3">
                                                <button onClick={handleCancelDependentEdit} className="text-gray-600 hover:text-gray-800 text-sm">Cancel</button>
                                                <button
                                                    onClick={handleSaveDependent}
                                                    className="bg-indigo-600 text-white px-3 py-1.5 rounded text-sm hover:bg-indigo-700"
                                                >
                                                    Save Dependent
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        dependents.length < 6 && (
                                            <button
                                                onClick={handleAddDependentStart}
                                                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors flex items-center justify-center font-medium"
                                            >
                                                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add Dependent
                                            </button>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Household Size Summary */}
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <div className="flex items-center justify-between">
                            <span className="font-semibold text-indigo-800">Total Household Size:</span>
                            <span className="text-2xl font-bold text-indigo-600">{enrollmentData.householdSize || 1}</span>
                        </div>
                        <p className="text-sm text-indigo-600 mt-1">
                            Client (1) {maritalStatus === '3' ? '+ Spouse (1)' : ''} {dependents.filter(d => d.onPolicy === 'Yes').length > 0 ? `+ Dependents (${dependents.filter(d => d.onPolicy === 'Yes').length})` : ''}
                        </p>
                    </div>
                </div>
            </div>
        </StageWrapper>
    )
}
